// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import LoginView from "@/views/LoginView.vue";
import DashboardView from "@/views/DashboardView.vue";
import CalendarView from "@/views/CalendarView.vue";
import ApplicationView from "@/views/ApplicationView.vue";
import HolidayApplicationView from "@/views/HolidayApplicationView.vue";
import DeskReservationView from "@/views/DeskReservationView.vue";
import DeskCalendarView from "@/views/DeskCalendarView.vue";
import HolidayApprovalView from "@/views/HolidayApprovalView.vue";
import HubRoomView from "@/views/HubRoomView.vue";
import AttendancePDFView from "@/views/AttendancePDFView.vue"; // 追加

const routes = [
  {
    path: "/",
    component: () => import("@/views/LoginView.vue"),
    meta: {
      title: "Login",
    },
  },
  {
    path: "/login",
    component: () => import("@/views/LoginView.vue"),
    meta: {
      title: "Login",
    },
  },
  {
    path: "/password-reset",
    component: () => import("@/views/PasswordResetRequestView.vue"),
    meta: {
      title: "Password Reset",
    },
  },
  {
    path: "/reset-password/:uidb64/:token",
    component: () => import("@/views/PasswordResetConfirmView.vue"),
    meta: {
      title: "Password Reset Confirmation",
    },
  },
  {
    path: "/password-change",
    component: () => import("@/views/PasswordChangeView.vue"),
    meta: {
      requiresAuth: true,
      title: "Change Password",
    },
  },
  {
    path: "/dashboard",
    component: () => import("@/views/DashboardView.vue"),
    meta: {
      requiresAuth: true,
      title: "Dashboard",
    },
  },
  {
    path: "/attendance/monthlycalendar",
    component: () => import("@/views/CalendarView.vue"),
    meta: {
      requiresAuth: true,
      title: "Calendar",
    },
  },
  {
    path: "/attendance/managers-view",
    component: () => import("@/views/ManagersView.vue"),
    meta: {
      requiresAuth: true,
      requiresManagerAccess: true,
      title: "Managers' View",
    },
  },
  {
    path: "/schedule",
    component: () => import("@/views/ScheduleView.vue"),
    meta: {
      requiresAuth: true,
      title: "Schedule",
    },
  },
  {
    path: "/application",
    component: () => import("@/views/ApplicationView.vue"),
    meta: {
      requiresAuth: true,
      title: "有給・書類申請 | 勤怠アプリ",
    },
  },
  {
    path: "/application/holiday",
    component: () => import("@/views/HolidayApplicationView.vue"),
    meta: {
      requiresAuth: true,
      title: "DayOff",
    },
  },
  {
    path: "/attendance/deskreservation",
    component: () => import("@/views/DeskReservationView.vue"),
    meta: {
      requiresAuth: true,
      title: "Reserve Desk",
    },
  },
  {
    path: "/attendance/deskcalendar",
    component: () => import("@/views/DeskCalendarView.vue"),
    meta: {
      requiresAuth: true,
      title: "Seat Calendar",
    },
  },
  {
    path: "/manage/holidayapproval",
    component: () => import("@/views/HolidayApprovalView.vue"),
    meta: {
      requiresAuth: true,
      requiresApprovalsAccess: true,
      title: "Approvals",
    },
  },
  {
    path: "/manage/company-holidays",
    component: () => import("@/views/CompanyHolidaysAdminView.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "Company Holidays",
    },
  },
  {
    path: "/manage/user",
    component: () => import("@/views/AdminManagementView.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "Admin Management",
    },
  },
  {
    path: "/manage/others",
    component: () => import("@/views/ManageOthersView.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "Manage Others",
    },
  },
  {
    path: "/manage/visiter",
    component: () => import("@/views/ManageVisiterView.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "IE Visitor Master",
    },
  },
  {
    path: "/attendance/hubroom",
    component: () => import("@/views/HubRoomView.vue"),
    meta: {
      requiresAuth: true,
      title: "Meeting Room",
    },
  },
  {
    path: "/attendance/visitor",
    component: () => import("@/views/VisitorView.vue"),
    meta: {
      requiresAuth: true,
      title: "IE Visitor",
    },
  },
  {
    path: "/manage/attendancepdf", // 追加
    component: () => import("@/views/AttendancePDFView.vue"), // 追加
    meta: {
      requiresAuth: true,
      requiresAttendanceAdmin: true, // 追加
      title: "勤怠管理",
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// ナビゲーションガード：認証が必要なページの場合、トークンがなければログインページにリダイレクト
router.beforeEach((to, from, next) => {
  if (to.meta.requiresAuth) {
    const token = sessionStorage.getItem("token");
    if (!token) {
      next({ path: "/" });
    } else {
      // 勤怠管理者権限が必要な場合
      if (to.meta.requiresAdmin) {
        const isAdminFlag = localStorage.getItem("isAdmin") === "true";
        if (!isAdminFlag) {
          next({ path: "/dashboard" });
        } else {
          next();
        }
      }
      else if (to.meta.requiresAttendanceAdmin) {
        const isAttendanceAdmin =
          localStorage.getItem("isAttendanceAdmin") === "true";
        if (!isAttendanceAdmin) {
          // 権限がない場合はダッシュボードにリダイレクト
          next({ path: "/dashboard" });
        } else {
          next();
        }
      }
      else if (to.meta.requiresManagerAccess) {
        const hasApprovalRightFlag =
          localStorage.getItem("hasApprovalRight") === "true";
        const isAdminFlag = localStorage.getItem("isAdmin") === "true";
        const isAttendanceAdminFlag =
          localStorage.getItem("isAttendanceAdmin") === "true";
        const isTopLevelFlag = localStorage.getItem("isTopLevel") === "true";
        if (
          !(
            hasApprovalRightFlag ||
            isAdminFlag ||
            isAttendanceAdminFlag ||
            isTopLevelFlag
          )
        ) {
          next({ path: "/dashboard" });
        } else {
          next();
        }
      }
      else if (to.meta.requiresApprovalsAccess) {
        const hasApprovalRightFlag =
          localStorage.getItem("hasApprovalRight") === "true";
        const isAdminFlag = localStorage.getItem("isAdmin") === "true";
        if (!(hasApprovalRightFlag || isAdminFlag)) {
          next({ path: "/dashboard" });
        } else {
          next();
        }
      }
      else {
        next();
      }
    }
  } else {
    next();
  }
});

router.afterEach((to) => {
  const defaultTitle = "My App";
  document.title = to.meta.title || defaultTitle;
});

export default router;
